import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CatalogueFournisseurService, {
  Fournisseur,
  UserFournisseur,
} from '../../services/CatalogueFournisseurService';
import CatalogueSyncService, { UpdatesInfo } from '../../services/CatalogueSyncService';

const FournisseursSection: React.FC = () => {
  const navigate = useNavigate();
  const [userFournisseurs, setUserFournisseurs] = useState<UserFournisseur[]>([]);
  const [fournisseursDisponibles, setFournisseursDisponibles] = useState<Fournisseur[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFournisseur, setCurrentFournisseur] = useState<UserFournisseur | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fournisseur: '',
    remise_globale: 0,
    conditions_paiement: '',
    notes: '',
  });

  const userId = 3; // TODO: Récupérer depuis le contexte utilisateur
  const tenantId = 2; // Sellerie Marine
  
  // State pour synchronisation
  const [updatesMap, setUpdatesMap] = useState<Map<string, UpdatesInfo>>(new Map());
  const [syncing, setSyncing] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (userFournisseurs.length > 0) {
      checkUpdates();
    }
  }, [userFournisseurs]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [userFourn, disponibles] = await Promise.all([
        CatalogueFournisseurService.getUserFournisseurs(userId),
        CatalogueFournisseurService.getFournisseursDisponibles(),
      ]);
      setUserFournisseurs(userFourn);
      setFournisseursDisponibles(disponibles);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const checkUpdates = async () => {
    try {
      const fournisseurs = userFournisseurs.map(f => f.nom);
      const updates = await CatalogueSyncService.checkAllUpdates(userId, fournisseurs);
      setUpdatesMap(updates);
    } catch (err: any) {
      console.error('Erreur check updates:', err);
    }
  };

  const handleSynchroniser = async (fournisseur: UserFournisseur) => {
    if (!window.confirm(`Synchroniser le catalogue ${fournisseur.nom} ?\n\nCeci importera tous les produits disponibles avec la remise de ${fournisseur.remise_globale}%.`)) {
      return;
    }

    try {
      setSyncing(fournisseur.nom);
      setError(null);
      
      const result = await CatalogueSyncService.synchroniser(
        userId,
        fournisseur.nom,
        {
          tenant_id: tenantId,
          categories: ['Tissus', 'Mousses', 'Accessoires'],
          remise_appliquee: fournisseur.remise_globale,
        }
      );

      setSuccess(
        `✅ Synchronisation réussie : ${result.nb_imports} import(s), ${result.nb_updates} mise(s) à jour`
      );
      
      // Rafraîchir les updates
      await checkUpdates();
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(null);
    }
  };

  const handleOpenForm = (fournisseur?: UserFournisseur) => {
    if (fournisseur) {
      setEditMode(true);
      setCurrentFournisseur(fournisseur);
      setFormData({
        fournisseur: fournisseur.nom,
        remise_globale: fournisseur.remise_globale,
        conditions_paiement: fournisseur.conditions_paiement || '',
        notes: fournisseur.notes || '',
      });
    } else {
      setEditMode(false);
      setCurrentFournisseur(null);
      setFormData({
        fournisseur: '',
        remise_globale: 0,
        conditions_paiement: '',
        notes: '',
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setCurrentFournisseur(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (editMode && currentFournisseur) {
        await CatalogueFournisseurService.updateUserFournisseur(userId, currentFournisseur.id, {
          remise_globale: formData.remise_globale,
          conditions_paiement: formData.conditions_paiement,
          notes: formData.notes,
        });
        setSuccess('Fournisseur mis à jour');
      } else {
        await CatalogueFournisseurService.addUserFournisseur(userId, formData);
        setSuccess('Fournisseur ajouté');
      }

      await loadData();
      handleCloseForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fournisseurId: number) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;

    try {
      setLoading(true);
      await CatalogueFournisseurService.deleteUserFournisseur(userId, fournisseurId);
      setSuccess('Fournisseur supprimé');
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          🏭 Mes Fournisseurs ({userFournisseurs.length})
        </h3>
        <button
          className="btn-primary text-sm"
          onClick={() => handleOpenForm()}
          disabled={loading}
        >
          + Ajouter fournisseur
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
          <button className="float-right" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Formulaire ajout/édition */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 bg-white p-4 rounded border border-blue-300">
          <h4 className="font-medium mb-3">
            {editMode ? 'Modifier' : 'Ajouter'} un fournisseur
          </h4>
          
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="form-label text-sm">Fournisseur *</label>
              <select
                className="input-field text-sm"
                value={formData.fournisseur}
                onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                disabled={editMode}
                required
              >
                <option value="">Sélectionner...</option>
                {fournisseursDisponibles.map((f) => (
                  <option key={f.nom} value={f.nom}>
                    {f.nom} ({f.nb_produits} produits)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label text-sm">Remise négociée (%) *</label>
              <input
                type="number"
                className="input-field text-sm"
                value={formData.remise_globale}
                onChange={(e) => setFormData({ ...formData, remise_globale: parseFloat(e.target.value) })}
                min="0"
                max="100"
                step="0.1"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="form-label text-sm">Conditions de paiement</label>
              <input
                type="text"
                className="input-field text-sm"
                value={formData.conditions_paiement}
                onChange={(e) => setFormData({ ...formData, conditions_paiement: e.target.value })}
                placeholder="Ex: 30 jours fin de mois"
              />
            </div>

            <div>
              <label className="form-label text-sm">Notes</label>
              <input
                type="text"
                className="input-field text-sm"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes internes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
              onClick={handleCloseForm}
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={loading || !formData.fournisseur}
            >
              {loading ? 'En cours...' : editMode ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </form>
      )}

      {/* Liste des fournisseurs */}
      {userFournisseurs.length === 0 ? (
        <p className="text-gray-600 text-sm text-center py-4">
          Aucun fournisseur configuré. Ajoutez vos fournisseurs pour accéder à leurs catalogues.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produits</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remise</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Conditions</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userFournisseurs.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{f.nom}</td>
                  <td className="px-4 py-3 text-gray-600">{f.nb_produits}</td>
                  <td className="px-4 py-3 text-gray-600">{f.remise_globale}%</td>
                  <td className="px-4 py-3 text-gray-600">{f.conditions_paiement || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{f.notes || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    {updatesMap.get(f.nom)?.has_updates && (
                      <span className="inline-flex items-center px-2 py-1 mr-2 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                        🔔 {updatesMap.get(f.nom)?.nb_versions_pending} mise(s) à jour
                      </span>
                    )}
                    <button
                      className="text-green-600 hover:text-green-800 mr-3 text-sm"
                      onClick={() => navigate(`/catalogue/${encodeURIComponent(f.nom)}`)}
                      title="Voir le catalogue"
                    >
                      📋 Catalogue
                    </button>
                    <button
                      className="text-purple-600 hover:text-purple-800 mr-3 text-sm"
                      onClick={() => handleSynchroniser(f)}
                      disabled={syncing === f.nom}
                      title="Synchroniser le catalogue"
                    >
                      {syncing === f.nom ? '⏳' : '🔄'} Synchroniser
                    </button>
                    <button
                      className="text-blue-600 hover:text-blue-800 mr-3 text-sm"
                      onClick={() => handleOpenForm(f)}
                      title="Modifier"
                    >
                      Modifier
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800 text-sm"
                      onClick={() => handleDelete(f.id)}
                      title="Supprimer"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-600">
        💡 <strong>Astuce :</strong> Configurez vos fournisseurs avec leurs remises pour importer facilement des produits dans votre catalogue.
      </p>
    </div>
  );
};

export default FournisseursSection;
